import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

// Normalize scroll for smooth scrollers like Lenis - critical for Mac/Windows parity
ScrollTrigger.normalizeScroll(true);

function lenisSetup() {
	const lenis = new Lenis({
		duration: 1.2,
		easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
		gestureDirection: 'vertical',
		smooth: true,
		smoothTouch: false,
		touchMultiplier: 2,
	});	

	// Configure ScrollTrigger to work with Lenis
	// This is critical for Mac/Windows consistency
	ScrollTrigger.defaults({
		scroller: document.documentElement
	});

	// Add Lenis's requestAnimationFrame (raf) method to GSAP's ticker
	// This ensures Lenis's smooth scroll animation updates on each GSAP tick
	gsap.ticker.add((time) => {
		lenis.raf(time * 1000);
	});

	// Disable lag smoothing in GSAP to prevent any delay in scroll animations
	gsap.ticker.lagSmoothing(0);

	// Prevent GSAP ticker from sleeping - critical for Safari to avoid animation pausing
	gsap.ticker.sleep(false);

	// Synchronize Lenis scrolling with GSAP's ScrollTrigger plugin
	lenis.on("scroll", ScrollTrigger.update);

	lenis.on("resize", () => {
		ScrollTrigger.refresh();
	});

	window.lenis = lenis;

	return lenis; // return instance for use in other modules
}

function lenisExceptions(instance) {
	const lenis = instance || window.lenis;

	// Process iframes to ensure they have the prevent attribute
	const processIframes = () => {
		const iframes = document.querySelectorAll('iframe:not([data-lenis-prevent])');
		iframes.forEach((iframe) => {
			iframe.setAttribute('data-lenis-prevent', 'true');
		});
	};

	// Function to process elements that should prevent Lenis/GSAP scroll interference
	const processPreventElements = () => {
		const elements = document.querySelectorAll('[data-lenis-prevent]');
		
		elements.forEach(el => {
			if (el.hasAttribute('data-lenis-processed')) return;
			
			// Force native scroll by stopping the event from bubbling to Lenis/GSAP
			// This is critical when ScrollTrigger.normalizeScroll(true) is active
			// AND it fixes the "wait for scroll to stop" issue by ensuring the event
			// is handled locally before GSAP/Lenis can see it.
			el.addEventListener('wheel', (e) => {
				e.stopPropagation();
			}, { passive: false });
			
			el.addEventListener('touchstart', (e) => {
				e.stopPropagation();
			}, { passive: false });
			
			el.addEventListener('touchmove', (e) => {
				e.stopPropagation();
			}, { passive: false });

			el.setAttribute('data-lenis-processed', 'true');
		});
	};

	// Search & Filter Pro dropdowns
	// We use a comprehensive approach with MutationObserver to catch dynamically created listboxes
	const processComboboxes = () => {
		const comboboxes = document.querySelectorAll('.search-filter-component-combobox-base');
		
		comboboxes.forEach(combobox => {
			// Prevent Lenis on the combobox itself
			if (!combobox.hasAttribute('data-lenis-prevent')) {
				combobox.setAttribute('data-lenis-prevent', 'true');
			}

			// Prevent Lenis on the associated listbox (the dropdown)
			const listboxId = combobox.getAttribute('aria-controls');
			if (listboxId) {
				const listbox = document.getElementById(listboxId);
				if (listbox && !listbox.hasAttribute('data-lenis-prevent')) {
					listbox.setAttribute('data-lenis-prevent', 'true');
				}
			}
		});
	};

	// Run initially
	processIframes();
	processComboboxes();
	processPreventElements();

	// Handle dynamically added content (e.g. S&F AJAX results, or listboxes created on demand)
	const observer = new MutationObserver((mutations) => {
		let shouldProcess = false;
		mutations.forEach((mutation) => {
			if (mutation.addedNodes.length > 0) {
				shouldProcess = true;
			}
		});
		
		if (shouldProcess) {
			processIframes();
			processComboboxes();
			processPreventElements();
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true
	});
}	

export { lenisSetup, lenisExceptions };
